import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, CreditCard as Edit3, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { proposalService, type Proposal } from '@/lib/proposalService';
import { ProposalRenderer } from '@/components/proposals/ProposalRenderer';
import { useWorkspace } from '@/context/WorkspaceContext';
import { exportElementToPdf } from '@/lib/proposalPdf';
import { toast } from 'sonner';

export default function ProposalPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [sender, setSender] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const docRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!id) return;
    proposalService.getProposal(id).then(async p => {
      if (p) {
        const { supabase } = await import('@/lib/supabase');
        const { data: senderProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', p.created_by)
          .maybeSingle();
        setSender(senderProfile);
      }
      setProposal(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const copyLink = () => {
    if (!proposal) return;
    const url = `${window.location.origin}/proposals/${proposal.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Client link copied');
  };

  const downloadPdf = async () => {
    if (!docRef.current || !proposal) return;
    toast.info('Generating PDF...');
    try {
      await exportElementToPdf(docRef.current, proposal.title || 'proposal');
      await proposalService.recordEvent(proposal.id, 'downloaded', { actor: 'agency' });
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading...</div>;
  if (!proposal) return <div className="py-20 text-center text-muted-foreground">Not found</div>;

  const appearance = currentWorkspace?.appearance;
  const agencyLogo = appearance?.logo || currentWorkspace?.logo;

  return (
    <div className="min-h-screen bg-transparent">
      <Helmet><title>{proposal.title} | Preview</title></Helmet>

      {/* Top bar — full width */}
      <div className="sticky top-0 z-20 bg-white dark:bg-card border-b border-gray-200 dark:border-border shadow-sm w-full">
        <div className="w-full px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => navigate('/proposals')}>
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Proposals</span>
            </Button>
            <span className="text-muted-foreground hidden sm:block">/</span>
            <span className="text-sm font-semibold truncate">{proposal.title}</span>
            <span className="text-xs text-muted-foreground capitalize hidden sm:block">· Preview</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {proposal.status === 'draft' && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/proposals/${proposal.id}/edit`)}>
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
            {proposal.status !== 'draft' && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={copyLink}>
                <LinkIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Copy client link</span>
              </Button>
            )}
            <Button size="sm" className="gap-1.5" onClick={downloadPdf}>
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download PDF</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className="py-10 px-6">
        <ProposalRenderer
          ref={docRef}
          proposal={proposal}
          agencyName={currentWorkspace?.name}
          agencyLogo={agencyLogo}
          sender={sender}
        />
      </div>
    </div>
  );
}
